import { useCallback } from "react";
import { useSelector } from "react-redux";
import { getCartDetails } from "../../store/cartDetails/selectors";
import useAvailableCheckoutSteps from "./useAvailableCheckoutSteps";
import useRouteName from "../useRouteName";
import { useCheckoutProcessor } from "../useCheckoutProcessor";
import useNextCheckoutScreen from "./useNextCheckoutScreen";
import useEvents from "../useEvents";
import { matchPath } from "react-router-dom";
import { UICartDetails } from "@flights/types";
import routeMap from "../../app/routes";
import { getFlight } from "../../store/flightDetails/selectors";
import { enableCheckoutStatus } from "../../utils/checkoutStatusStorage";

const findUrlMatch = () => {
  const isMobile = window.__GLOBAL_CONTEXT__.userAgent.isMobile;
  const isPaymentCollectionPage = window.__GLOBAL_CONTEXT__.isPaymentCollectionPage;
  const isWebview = window.__GLOBAL_CONTEXT__.userAgent.isWebview || false;
  const isAlternativeSeatmap = window.__GLOBAL_CONTEXT__.isAlternativeSeatmap || false;

  for (const route of routeMap({
    isMobile,
    isPaymentCollectionPage,
    isWebview,
    isAlternativeSeatmap
  })) {
    const match = matchPath(window.location.pathname, route);
    if (match) {
      return match;
    }
  }

  throw new Error(`Current path <${window.location.pathname}> doesnt match a known route`);
};

export const getScreenUrl = (data: UICartDetails) => {
  const routeMatch = findUrlMatch();

  let newRoute = routeMatch.path;

  Object.entries(routeMatch.params).forEach(([key, value]) => {
    if (key === "cartToken" && data.cart.cartToken) {
      newRoute = newRoute.replace(`:${key}`, data.cart.cartToken);
    } else {
      newRoute = newRoute.replace(`:${key}`, (value as string) || "");
    }
  });

  return newRoute;
};

const useCheckoutNavigation = () => {
  const flight = useSelector(getFlight);
  const cartDetails = useSelector(getCartDetails);

  const steps = useAvailableCheckoutSteps();
  const currentRoute = useRouteName();
  const checkout = useCheckoutProcessor();
  const { goNextCheckoutScreen } = useNextCheckoutScreen();

  const { jsError } = useEvents();

  const currentStepIndex = steps.findIndex(({ step }) => step === currentRoute);
  const nextStep: typeof steps[number] | undefined = steps[currentStepIndex + 1];

  const prepareNextCheckoutScreen = useCallback(
    async (data?: { spanishIslandsOptionalData?: unknown }) => {
      try {
        if (nextStep?.requiresAncillaries && !cartDetails?.cart.extras) {
          await checkout.addSelectedProductsToCart(cartDetails?.cart.cartToken);
        }

        if (nextStep?.requiresOrder) {
          await checkout.createOrder(cartDetails?.cart, {
            basketReference: cartDetails?.basketReference,
            ...(data?.spanishIslandsOptionalData || {})
          });
        }
      } catch (error) {
        jsError(error);
        throw error;
      }

      if (flight?.token && (nextStep?.requiresAncillaries || nextStep?.requiresOrder)) {
        enableCheckoutStatus("requiresCartReset");
      }

      return goNextCheckoutScreen;
    },
    [
      cartDetails?.basketReference,
      cartDetails?.cart,
      checkout,
      flight?.token,
      goNextCheckoutScreen,
      jsError,
      nextStep?.requiresAncillaries,
      nextStep?.requiresOrder
    ]
  );

  return prepareNextCheckoutScreen;
};

export default useCheckoutNavigation;
